// FHIR R4-aligned types for specialist referrals

export interface FHIRCoding {
  system: string;
  code: string;
  display: string;
}

export interface FHIRHumanName {
  use: 'official' | 'usual';
  family: string;
  given: string[];
  prefix?: string[];
}

export interface FHIRContactPoint {
  system: 'phone' | 'email' | 'fax';
  value: string;
  use: 'work' | 'home' | 'mobile';
}

export interface FHIRAddress {
  use: 'work' | 'home';
  line: string[];
  city: string;
  state: string;
  postalCode: string;
}

export interface FHIRQualification {
  code: FHIRCoding;
  issuer?: string;
}

export interface FHIRPractitioner {
  resourceType: 'Practitioner';
  id: string;
  name: FHIRHumanName[];
  gender?: 'male' | 'female' | 'other';
  telecom: FHIRContactPoint[];
  address: FHIRAddress[];
  qualification: FHIRQualification[];
}

export interface FHIRAvailableTime {
  daysOfWeek: string[];
  availableStartTime: string;
  availableEndTime: string;
}

export interface FHIRPractitionerRole {
  resourceType: 'PractitionerRole';
  id: string;
  specialty: FHIRCoding[];
  availableTime?: FHIRAvailableTime[];
}

export interface FHIRSlot {
  resourceType: 'Slot';
  id: string;
  status: 'free' | 'busy' | 'busy-unavailable';
  start: string;
}

export interface FHIRServiceRequest {
  resourceType: 'ServiceRequest';
  id: string;
  status: 'draft' | 'active' | 'completed';
  intent: 'proposal' | 'plan' | 'order';
  subject: string;
  performer: string;
  reasonCode: FHIRCoding[];
  note: string;
  authoredOn: string;
}

export interface SpecialistEntry {
  practitioner: FHIRPractitioner;
  role: FHIRPractitionerRole;
  nextAvailableSlot: FHIRSlot;
}

// Helper to generate a future date string
const futureDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString();
};

export const specialists: SpecialistEntry[] = [
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-001',
      name: [
        {
          use: 'official',
          family: 'Chen',
          given: ['Wei'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 234-5678', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['100 Heart Center Blvd'],
          city: 'San Francisco',
          state: 'CA',
          postalCode: '94102',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-001',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394579002',
          display: 'Cardiology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'wed', 'fri'],
          availableStartTime: '09:00',
          availableEndTime: '17:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-001',
      status: 'free',
      start: futureDate(3),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-002',
      name: [
        {
          use: 'official',
          family: 'Patel',
          given: ['Anita'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 345-6789', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['250 Dermatology Way'],
          city: 'Los Angeles',
          state: 'CA',
          postalCode: '90001',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-002',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394582007',
          display: 'Dermatology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['tue', 'thu'],
          availableStartTime: '08:00',
          availableEndTime: '16:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-002',
      status: 'free',
      start: futureDate(5),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-003',
      name: [
        {
          use: 'official',
          family: 'Rodriguez',
          given: ['Carlos'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 456-7890', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['500 Ortho Center Dr'],
          city: 'Chicago',
          state: 'IL',
          postalCode: '60601',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-003',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394801008',
          display: 'Orthopedics',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
          availableStartTime: '07:00',
          availableEndTime: '15:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-003',
      status: 'free',
      start: futureDate(7),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-004',
      name: [
        {
          use: 'official',
          family: 'Kim',
          given: ['Soo-Jin'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 567-8901', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['720 Neuro Plaza'],
          city: 'Boston',
          state: 'MA',
          postalCode: '02101',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-004',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394591006',
          display: 'Neurology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'wed', 'thu'],
          availableStartTime: '10:00',
          availableEndTime: '18:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-004',
      status: 'free',
      start: futureDate(2),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-005',
      name: [
        {
          use: 'official',
          family: 'Okafor',
          given: ['Emeka'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 678-9012', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['890 GI Health Center'],
          city: 'Houston',
          state: 'TX',
          postalCode: '77001',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-005',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394584008',
          display: 'Gastroenterology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['tue', 'thu', 'fri'],
          availableStartTime: '08:30',
          availableEndTime: '16:30',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-005',
      status: 'free',
      start: futureDate(10),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-006',
      name: [
        {
          use: 'official',
          family: 'Nakamura',
          given: ['Yuki'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 789-0123', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['340 Endocrine Institute'],
          city: 'Seattle',
          state: 'WA',
          postalCode: '98101',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-006',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394583002',
          display: 'Endocrinology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'tue', 'wed'],
          availableStartTime: '09:00',
          availableEndTime: '17:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-006',
      status: 'free',
      start: futureDate(14),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-007',
      name: [
        {
          use: 'official',
          family: 'Thompson',
          given: ['Margaret'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 890-1234', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['460 Pulmonary Center'],
          city: 'Denver',
          state: 'CO',
          postalCode: '80201',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-007',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394600006',
          display: 'Pulmonology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['wed', 'thu', 'fri'],
          availableStartTime: '08:00',
          availableEndTime: '16:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-007',
      status: 'free',
      start: futureDate(4),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-008',
      name: [
        {
          use: 'official',
          family: 'Gupta',
          given: ['Ravi'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 901-2345', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['580 Rheumatology Clinic'],
          city: 'Philadelphia',
          state: 'PA',
          postalCode: '19101',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-008',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394810000',
          display: 'Rheumatology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'thu'],
          availableStartTime: '09:30',
          availableEndTime: '17:30',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-008',
      status: 'free',
      start: futureDate(21),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-009',
      name: [
        {
          use: 'official',
          family: 'Johannsen',
          given: ['Erik'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 012-3456', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['700 Oncology Tower'],
          city: 'New York',
          state: 'NY',
          postalCode: '10001',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-009',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394593009',
          display: 'Oncology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'tue', 'wed', 'thu'],
          availableStartTime: '08:00',
          availableEndTime: '16:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-009',
      status: 'free',
      start: futureDate(12),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-010',
      name: [
        {
          use: 'official',
          family: 'Abadi',
          given: ['Leila'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 123-4567', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['820 Vision Care Center'],
          city: 'Miami',
          state: 'FL',
          postalCode: '33101',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-010',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394594003',
          display: 'Ophthalmology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'wed', 'fri'],
          availableStartTime: '09:00',
          availableEndTime: '17:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-010',
      status: 'free',
      start: futureDate(6),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-011',
      name: [
        {
          use: 'official',
          family: 'Martinez',
          given: ['Sofia'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 234-5679', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['150 Cardiac Specialists'],
          city: 'San Diego',
          state: 'CA',
          postalCode: '92101',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-011',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394579002',
          display: 'Cardiology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['tue', 'thu'],
          availableStartTime: '10:00',
          availableEndTime: '18:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-011',
      status: 'free',
      start: futureDate(8),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-012',
      name: [
        {
          use: 'official',
          family: 'Brennan',
          given: ['Patrick'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 345-6780', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['300 Skin Health Institute'],
          city: 'Portland',
          state: 'OR',
          postalCode: '97201',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-012',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394582007',
          display: 'Dermatology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'wed', 'fri'],
          availableStartTime: '08:30',
          availableEndTime: '16:30',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-012',
      status: 'free',
      start: futureDate(15),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-013',
      name: [
        {
          use: 'official',
          family: 'Owusu',
          given: ['Kwame'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 456-7891', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['420 Neuro Sciences Bldg'],
          city: 'Atlanta',
          state: 'GA',
          postalCode: '30301',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-013',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394591006',
          display: 'Neurology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['tue', 'wed', 'thu'],
          availableStartTime: '09:00',
          availableEndTime: '17:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-013',
      status: 'free',
      start: futureDate(9),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-014',
      name: [
        {
          use: 'official',
          family: 'Ivanova',
          given: ['Elena'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 567-8902', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['610 Joint & Bone Center'],
          city: 'Phoenix',
          state: 'AZ',
          postalCode: '85001',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-014',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394801008',
          display: 'Orthopedics',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'fri'],
          availableStartTime: '07:30',
          availableEndTime: '15:30',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-014',
      status: 'free',
      start: futureDate(18),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-015',
      name: [
        {
          use: 'official',
          family: 'Liu',
          given: ['Mei'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 678-9013', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['780 Digestive Health Clinic'],
          city: 'San Jose',
          state: 'CA',
          postalCode: '95101',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-015',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394584008',
          display: 'Gastroenterology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'wed', 'thu'],
          availableStartTime: '08:00',
          availableEndTime: '16:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-015',
      status: 'free',
      start: futureDate(11),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-016',
      name: [
        {
          use: 'official',
          family: 'Al-Farsi',
          given: ['Omar'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 789-0124', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['200 Diabetes & Hormone Center'],
          city: 'Dallas',
          state: 'TX',
          postalCode: '75201',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-016',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394583002',
          display: 'Endocrinology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['tue', 'wed', 'fri'],
          availableStartTime: '09:00',
          availableEndTime: '17:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-016',
      status: 'free',
      start: futureDate(20),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-017',
      name: [
        {
          use: 'official',
          family: 'Wilson',
          given: ['James'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 890-1235', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['550 Lung & Respiratory Clinic'],
          city: 'Nashville',
          state: 'TN',
          postalCode: '37201',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-017',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394600006',
          display: 'Pulmonology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'tue', 'thu'],
          availableStartTime: '08:00',
          availableEndTime: '16:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-017',
      status: 'free',
      start: futureDate(30),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-018',
      name: [
        {
          use: 'official',
          family: 'Park',
          given: ['Min-Ji'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 901-2346', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['440 Autoimmune Center'],
          city: 'Minneapolis',
          state: 'MN',
          postalCode: '55401',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-018',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394810000',
          display: 'Rheumatology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['wed', 'thu', 'fri'],
          availableStartTime: '09:30',
          availableEndTime: '17:30',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-018',
      status: 'free',
      start: futureDate(25),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-019',
      name: [
        {
          use: 'official',
          family: 'Adebayo',
          given: ['Folake'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'female',
      telecom: [{ system: 'phone', value: '(555) 012-3457', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['900 Cancer Treatment Center'],
          city: 'Baltimore',
          state: 'MD',
          postalCode: '21201',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-019',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394593009',
          display: 'Oncology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
          availableStartTime: '07:00',
          availableEndTime: '15:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-019',
      status: 'free',
      start: futureDate(42),
    },
  },
  {
    practitioner: {
      resourceType: 'Practitioner',
      id: 'pract-020',
      name: [
        {
          use: 'official',
          family: 'Schneider',
          given: ['Hans'],
          prefix: ['Dr.'],
        },
      ],
      gender: 'male',
      telecom: [{ system: 'phone', value: '(555) 123-4568', use: 'work' }],
      address: [
        {
          use: 'work',
          line: ['660 Eye Institute'],
          city: 'Austin',
          state: 'TX',
          postalCode: '73301',
        },
      ],
      qualification: [
        {
          code: {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0360',
            code: 'MD',
            display: 'Doctor of Medicine',
          },
        },
      ],
    },
    role: {
      resourceType: 'PractitionerRole',
      id: 'role-020',
      specialty: [
        {
          system: 'http://snomed.info/sct',
          code: '394594003',
          display: 'Ophthalmology',
        },
      ],
      availableTime: [
        {
          daysOfWeek: ['tue', 'thu'],
          availableStartTime: '09:00',
          availableEndTime: '17:00',
        },
      ],
    },
    nextAvailableSlot: {
      resourceType: 'Slot',
      id: 'slot-020',
      status: 'free',
      start: futureDate(16),
    },
  },
];

// Helper to get a formatted display name
export const getSpecialistDisplayName = (entry: SpecialistEntry): string => {
  const name = entry.practitioner.name[0];
  const prefix = name?.prefix?.[0] ?? '';
  return `${prefix} ${name?.given.join(' ')} ${name?.family}`.trim();
};

// Helper to get the primary specialty display name
export const getSpecialtyDisplay = (entry: SpecialistEntry): string => {
  return entry.role.specialty[0]?.display ?? 'General Practice';
};

// Helper to get formatted address
export const getAddressDisplay = (entry: SpecialistEntry): string => {
  const addr = entry.practitioner.address[0];
  if (!addr) return '';
  return `${addr.line.join(', ')}, ${addr.city}, ${addr.state} ${addr.postalCode}`;
};

// Helper to get phone number
export const getPhoneDisplay = (entry: SpecialistEntry): string => {
  const phone = entry.practitioner.telecom.find((t) => t.system === 'phone');
  return phone?.value ?? '';
};

// Format the next available date relative to now
export const formatNextAvailable = (slotStart: string): string => {
  const slotDate = new Date(slotStart);
  const now = new Date();
  const diffMs = slotDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 1) return 'Next available: Tomorrow';
  if (diffDays <= 7) return `Next available: in ${diffDays} days`;
  if (diffDays <= 14)
    return `Next available: in ${Math.ceil(diffDays / 7)} week`;

  const weeks = Math.ceil(diffDays / 7);
  return `Next available: in ${weeks} weeks`;
};

// Generate a fake referral letter
export const generateReferralNote = (
  specialist: SpecialistEntry,
  reasonForReferral: string,
  physicianName?: string,
  patientName?: string,
): FHIRServiceRequest => {
  const specialistName = getSpecialistDisplayName(specialist);
  const specialty = getSpecialtyDisplay(specialist);
  const address = getAddressDisplay(specialist);
  const physician = physicianName ?? 'Referring Physician';
  const patient = patientName ?? 'my patient';

  const letter = [
    `Dear ${specialistName},`,
    '',
    `I am writing to refer ${patientName ? patientName : 'my patient'} for a ${specialty} consultation.`,
    '',
    `Reason for referral: ${reasonForReferral || 'Clinical evaluation and management as deemed appropriate.'}`,
    '',
    `${patientName ? patientName + ' has' : 'The patient has'} been under my care and I believe they would benefit from your specialized expertise. Please find the relevant clinical history and documentation attached.`,
    '',
    `${patientName ? patientName + ' has' : 'The patient has'} been advised to schedule an appointment at your office located at ${address}.`,
    '',
    'Please do not hesitate to contact my office if you require any additional information regarding this patient.',
    '',
    'Thank you for your time and consideration.',
    '',
    'Sincerely,',
    `Dr. ${physician}`,
  ].join('\n');

  return {
    resourceType: 'ServiceRequest',
    id: `ref-${specialist.practitioner.id}-${Date.now()}`,
    status: 'draft',
    intent: 'order',
    subject: patient,
    performer: specialistName,
    reasonCode: [
      {
        system: 'http://snomed.info/sct',
        code: '3457005',
        display: reasonForReferral || 'Patient referral',
      },
    ],
    note: letter,
    authoredOn: new Date().toISOString(),
  };
};
